import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Divider, Grid } from '@mui/material';
import type { ApiTrainingItem } from '@agunity/api-v4';

import ContentModal from './ContentModal';
import TrainingTable from './TrainingTable';
import { AgColor } from '../../stories/Styles/AgColor';
import AgLoadingCard from '../../stories/AgLoadingCard';
import AgTypography from '../../stories/Styles/AgTypography';

import { getTrainings } from './redux';
import { useAppDispatch } from '../../redux/hooks';
import { useModal } from '../../redux/hooks/modal';
import * as S from './styled';

type Training = {
  canEdit?: boolean;
  userId: number;
  noPadding?: boolean;
}

const Training = ({ canEdit, userId, noPadding }: Training) => {
  const messages = useIntl().formatMessage;
  const { openModal, closeModal } = useModal();

  const dispatch = useAppDispatch();

  const [trainings, setTrainings] = useState<ApiTrainingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get all training items
  const fetchTrainings = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await dispatch(getTrainings(userId));
      setTrainings(data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userId]);

  // Trainings modal form
  const showViewForm = useCallback(async (trainingId?: number, userId?: number, topicName?: string) => {
    openModal({
      closeIcon: true,
      autoClose: false,
      maxHeight: 'calc(100vh - 100px)',
      title: messages({ id: 'TRAINING_VIEW_MODAL_TITLE' }),
      body: <ContentModal topicName={topicName} canEdit={canEdit} closeModal={closeModal} isAdd={!trainingId} trainingId={trainingId!} userId={userId!} callback={fetchTrainings} />
    });
  }, [canEdit, closeModal, fetchTrainings, messages, openModal]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchTrainings());
    return () => clearTimeout(debounce);
  }, [fetchTrainings]);

  return (
    <S.Main noPadding={noPadding}>
      <Grid container gap={2}>
        <Grid item xs>
          <AgTypography
            contentStyle='AP.H5.UPPERCASE.Bold'
            message={messages({ id: 'TRAINING' })}
            style={{ color: AgColor.grey6 }}
          />

          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            message={messages({ id: 'TRAINING_DESCRIPTION' })}
            style={{ marginTop: '8px', color: AgColor.grey7 }}
          />
        </Grid>

        <Grid item xs={12}>
          {isLoading
            ? (
              <AgLoadingCard
                text={messages({ id: 'LOADING' })}
                style={{ position: 'relative', height: '60vh' }}
              />
            )
            : trainings.length === 0 ? (
              <AgTypography contentStyle={'AP.Paragraph.Small.SemiBold'} style={{ color: AgColor.grey7 }} >
                {messages({ id: 'TRAINING_NO_TRAININGS' })}
              </AgTypography>
            ) : (
                <TrainingTable
                  canEdit={canEdit}
                  trainings={trainings}
                  onEdit={({ id, topicName }) => showViewForm(id, userId, topicName!)}
                />
              )
          }
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ margin: '16px 0', color: AgColor.grey4 }} />
        </Grid>

      </Grid>
    </S.Main>
  );
};

export default React.memo(Training);